import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryJson, RolodexEntryType } from 'types'

/**
 * Weld scan geometry from Maxweld for auto-populating scan range
 */
export interface WeldScanGeometry {
  x_start: number
  x_end: number
  y_start: number
  y_end: number
  unit: string
}

/**
 * Custom info structure for weld indications from Maxweld
 */
export interface WeldIndicationCustomInfo {
  damage_mechanism?: string
  acceptance?: string
  location?: {
    startX: number
    endX: number
    startY: number
    endY: number
    startZ: number
    endZ: number
  }
  z_at_max?: number
  z_index?: number
  gain_db?: number
  comments?: string
  max_amplitude?: number
  is_solution?: boolean
  created_at?: string
  file_name?: string
  weld_scan_geometry?: WeldScanGeometry | null
}

/**
 * Weld indication item with typed custom info
 */
export interface WeldIndication {
  id: string
  title: string
  tags: Record<string, string | null>
  customInfo: WeldIndicationCustomInfo
}

/**
 * Return type for the useWeldIndications hook
 */
interface UseWeldIndicationsResult {
  weldIndicationsByInspectionId: ReadonlyMap<string, WeldIndication[]>
  flatWeldIndications: WeldIndication[]
  isLoading: boolean
}

/**
 * Hook to fetch weld indications from Maxweld
 *
 * This hook:
 * - Fetches indications from provided weld indication set IDs
 * - Groups them by inspection ID
 * - Sorts by Z position (along-weld direction)
 *
 * @param weldIndicationSetIds - Array of weld indication set IDs to query
 * @param inspectionIds - Array of inspection IDs for grouping
 * @returns Weld indications grouped by inspection, flat list, and loading state
 */
export const useWeldIndications = (
  weldIndicationSetIds: string[] | null,
  inspectionIds: string[] | null
): UseWeldIndicationsResult => {
  // Fetch indications from weld indication sets
  const { entries, isLoading: indicationsLoading } = useRolodexEntries(
    weldIndicationSetIds && weldIndicationSetIds.length > 0
      ? [
          {
            type: RolodexEntryType.INDICATION,
            tags: {
              'indication-set': weldIndicationSetIds,
            },
          },
        ]
      : undefined
  )

  // Convert entries to weld indications
  const rolodexWeldIndications = useMemo(() => {
    return entries
      .filter((entry) => entry.type === RolodexEntryType.INDICATION)
      .map((entry: RolodexEntryJson) => ({
        id: entry.id,
        title:
          (entry.data.title as string) || `Indication ${entry.id.slice(0, 8)}`,
        tags: entry.tags,
        customInfo: (entry.data.custom_info || {}) as WeldIndicationCustomInfo,
      }))
  }, [entries])

  // Group by inspection ID and sort by Z position
  const weldIndicationsByInspectionId = useMemo(() => {
    const map = new Map<string, WeldIndication[]>()

    if (!inspectionIds) return map

    inspectionIds.forEach((id) => map.set(id, []))

    rolodexWeldIndications.forEach((indication) => {
      const inspectionTag = indication.tags.inspection
      if (inspectionTag && map.has(inspectionTag)) {
        map.get(inspectionTag)!.push(indication)
      }
    })

    // Sort each inspection's indications by Z position
    map.forEach((indications, inspectionId) => {
      map.set(
        inspectionId,
        indications.sort((a, b) => {
          const aZ = a.customInfo.location?.startZ ?? 0
          const bZ = b.customInfo.location?.startZ ?? 0
          return aZ - bZ
        })
      )
    })

    return map as ReadonlyMap<string, WeldIndication[]>
  }, [inspectionIds, rolodexWeldIndications])

  const flatWeldIndications = useMemo(() => {
    return Array.from(weldIndicationsByInspectionId.values()).flat()
  }, [weldIndicationsByInspectionId])

  return {
    weldIndicationsByInspectionId,
    flatWeldIndications,
    isLoading: indicationsLoading,
  }
}
